import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Quote, BadgeCheck, Star } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  id: 1,
  author: "Alex M.",
  quote: "StrideVault has the best selection of performance sneakers. The dark aesthetic makes shopping feel exclusive.",
  position: "Professional Athlete",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1564626/img/athlete_alex_m.png",
  rating: 5
}, {
  id: 2,
  author: "Jamie L.",
  quote: "Fast delivery and top-quality shoes. Absolutely my go-to for casual and training footwear.",
  position: "Fitness Enthusiast",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1564626/img/athlete_jamie_l.png",
  rating: 5
}, {
  id: 3,
  author: "Marcus T.",
  quote: "The limited drops are handled perfectly. No bots, just fair chances for real athletes to get the gear they need.",
  position: "Track & Field Coach",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1564626/img/athlete_marcus_t.png",
  rating: 5
}, {
  id: 4,
  author: "Sarah K.",
  quote: "I've never had a better fit. The size guide is spot on, and the performance lab recommendations changed my running game.",
  position: "Marathon Runner",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1564626/img/athlete_sarah_k.png",
  rating: 5
}];
export default function AthleteFeedbackLoop() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="athlete-feedback-loop" ref={ref} className={`dark bg-background text-foreground py-24 relative overflow-hidden transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-secondary/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-primary/5 rounded-full blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="mb-16 text-center max-w-3xl mx-auto">
          <Badge variant="outline" className="mb-4 border-primary/50 text-primary uppercase tracking-widest">
            Verified Reviews
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
            Athlete <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Feedback Loop</span>
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl">
            Hear from the professionals and enthusiasts who trust StrideVault for their performance needs.
          </p>
        </div>

        <Carousel opts={{
        align: "start",
        loop: true
      }} className="w-full max-w-7xl mx-auto">
          <CarouselContent className="-ml-4 md:-ml-6">
            {testimonials.map((item, i) => <CarouselItem key={item.id} className="pl-4 md:pl-6 md:basis-1/2 lg:basis-1/3" data-index={i}>
                <div className="h-full">
                  <Card className="h-full bg-white/5 backdrop-blur-xl border-white/10 shadow-2xl hover:border-secondary/30 transition-colors duration-300 group overflow-hidden relative">
                    {}
                    <Quote className="absolute top-6 right-6 w-24 h-24 text-secondary/10 rotate-12 group-hover:text-secondary/20 transition-colors duration-500 pointer-events-none" />
                    
                    <CardContent className="p-8 flex flex-col h-full relative z-10">
                      <div className="flex items-center gap-4 mb-8">
                        <div className="relative">
                          <div className="rounded-full p-1 bg-gradient-to-br from-primary/20 to-secondary/20">
                            <SafeHydrate><Avatar className="w-16 h-16 border-2 border-background/50">
                              <AvatarImage src={item.image} alt={item.author} className="object-cover" />
                              <AvatarFallback className="bg-muted text-muted-foreground text-xl font-bold">
                                {item.author.charAt(0)}
                              </AvatarFallback>
                            </Avatar></SafeHydrate>
                          </div>
                          <div className="absolute -bottom-1 -right-1 bg-background rounded-full p-0.5">
                            <BadgeCheck className="w-6 h-6 text-secondary fill-background" />
                          </div>
                        </div>
                        <div>
                          <h3 className="font-bold text-lg text-foreground">{item.author}</h3>
                          <p className="text-sm text-primary font-medium">{item.position}</p>
                          <div className="flex gap-0.5 mt-1">
                            {[...Array(item.rating)].map((_, starIndex) => <Star key={starIndex} className="w-3 h-3 fill-secondary text-secondary" />)}
                          </div>
                        </div>
                      </div>

                      <blockquote className="flex-grow">
                        <p className="text-lg md:text-xl leading-relaxed font-medium text-white/90">
                          "{item.quote}"
                        </p>
                      </blockquote>

                      <div className="mt-8 pt-6 border-t border-white/5 flex items-center justify-between">
                        <Badge variant="secondary" className="bg-secondary/10 text-secondary hover:bg-secondary/20 border-0">
                          Verified Pro
                        </Badge>
                        <span className="text-xs text-muted-foreground font-mono uppercase tracking-wider">
                          StrideVault Athlete
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </CarouselItem>)}
          </CarouselContent>
          
          <div className="flex justify-center gap-4 mt-12">
            <CarouselPrevious className="static translate-y-0 bg-white/5 border-white/10 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300" />
            <CarouselNext className="static translate-y-0 bg-white/5 border-white/10 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300" />
          </div>
        </Carousel>
      </div>
    </section>;
}